import { ApolloClient, gql } from 'apollo-boost';
import { GetUserProgramsResponse, GetUserProgramsVars } from './types';
import { ContributionEligibility } from '../ReportsBeta/ContributionEligibility/ContributionEligibilitySelector';

const getUserProgramsMutation = gql`
  mutation getUserPrograms(
    $search: String
    $status: String
    $stage: String
    $first: Int
    $offset: Int
  ) {
    getUserPrograms(
      search: $search
      status: $status
      stage: $stage
      first: $first
      offset: $offset
    ) {
      success
      totalCount
      programs {
        program {
          dbId
          name
          status
          visibility
          isCompleted
          contributionEligibility
          logoFileUrl
          managerId
        }
        manager {
          id
          firstName
          lastName
          email
        }
        programAnswers {
          dbId
          questionId
          text
          date1
          date2
          answerId
          enumResponse {
            dbId
            response
            responseValue
            responseType
            questionId
          }
        }
        reports {
          dbId
          name
          status
          stage
          startDate
          endDate
          allocations {
            edges {
              node {
                investment {
                  reportDate5
                }
              }
            }
          }
          reportingType
          optedInFullReporting
          bbStarted
          cnStarted
          year
          feedbackType
          donorName
          budget
          totalDonations
          sponsorName
          visibility
          results {
            edges {
              node {
                id
                enabled
                reportId
                seen
                indicatorId
                successCriteria
                forecastInitial
                forecastCurrent
                classification
                description
                relevant
                indicator {
                  id
                  name
                  stageId
                  definition
                  customizable
                  unit
                  definitionStart
                  definitionEnd
                  serviceGoal
                  initialForecastStart
                  initialForecastEnd
                  initialMeasurementStart
                  initialMeasurementEnd
                  impactReceiptText
                  outcome
                  forecastOutcome
                  finalOutcome
                  finalForecastStart
                  finalForecastEnd
                  finalMeasurementStart
                  finalMeasurementEnd
                  isHuman
                  guidance
                  group
                  measurementTypeId
                  benchmarkType
                  version
                  measurementType {
                    id
                    name
                  }
                  dbId
                  stgName
                }
                forecastVariance
                currentForecastFundedClaim
                currentForecastFullClaim
                currentForecastCatalyzed
                leverage
                sroiPer10kFundedClaim
                sroiPer10kFullClaim
                dbId
                attachment
                attachmentId
              }
            }
          }
        }
      }
    }
  }
`;

export const getUserPrograms = (
  client: ApolloClient<object>,
  variables: GetUserProgramsVars,
) => {
  return client.mutate<GetUserProgramsResponse>({
    mutation: getUserProgramsMutation,
    variables,
    fetchPolicy: 'no-cache',
  });
};

const disableProgramMutation = gql`
  mutation disableProgram($id: Int!) {
    disableProgram(id: $id) {
      success
    }
  }
`;

export interface DisableProgramResponse {
  disableProgram: {
    success: boolean;
  };
}

export const disableProgram = (client: ApolloClient<object>, id: number) => {
  return client.mutate<DisableProgramResponse>({
    mutation: disableProgramMutation,
    variables: { id },
    fetchPolicy: 'no-cache',
  });
};

const updateProgramStatusMutation = gql`
  mutation ToggleProgramStatus($id: Int!, $status: String!) {
    updateProgram(id: $id, status: $status) {
      success
      program {
        id
        status
      }
    }
  }
`;

export interface UpdateProgramStatusResponse {
  updateProgram: {
    success: boolean;
    program: {
      id: number;
      status: string;
    } | null;
  };
}

const updateProgramContributionEligibilityMutation = gql`
  mutation SetProgramContributionEligibility(
    $id: Int!
    $contributionEligibility: String!
  ) {
    updateProgram(id: $id, contributionEligibility: $contributionEligibility) {
      success
      program {
        id
        contributionEligibility
      }
    }
  }
`;

export interface UpdateProgramContributionEligibilityResponse {
  updateProgram: {
    success: boolean;
    program: { id: number; contributionEligibility: string | null } | null;
  };
}

export const updateProgramContributionEligibility = (
  client: ApolloClient<object>,
  id: number,
  contributionEligibility: ContributionEligibility,
) => {
  return client.mutate<UpdateProgramContributionEligibilityResponse>({
    mutation: updateProgramContributionEligibilityMutation,
    variables: { id, contributionEligibility },
    fetchPolicy: 'no-cache',
  });
};

export const updateProgramStatus = (
  client: ApolloClient<object>,
  id: number,
  status: 'active' | 'archived',
) => {
  return client.mutate<UpdateProgramStatusResponse>({
    mutation: updateProgramStatusMutation,
    variables: { id, status },
    fetchPolicy: 'no-cache',
  });
};
